import { Injectable, Type } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import Swal from 'sweetalert2';
import { ErrorInput, ErrorService } from './error.service';
import { Action } from '../models/action.model';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { DrawerData, DrawerFacadeService } from './drawer-facade.service';
import { ConfirmationDrawerComponent } from '@app/@shared/confirmation-drawer/confirmation-drawer.component';

enum OS {
  UNRECOGNIZED = 0,
  ANDROID = 1,
  IOS = 2,
}

interface DialogRedirectAction {
  redirectUrl?: string;
  reload?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CoreService {
  private readonly snackBarConfig: MatSnackBarConfig = {
    duration: 4000,
    panelClass: 'ahoy-notify',
    verticalPosition: 'bottom',
    horizontalPosition: 'end',
  };

  constructor(
    private router: Router,
    private errorService: ErrorService,
    private translateService: TranslateService,
    readonly snackBar: MatSnackBar,
    private httpClient: HttpClient,
    private drawerFacadeService: DrawerFacadeService,
    private dialog: MatDialog,
  ) {}

  openDrawer<T>(component: Type<T>, data: DrawerData) {
    this.drawerFacadeService.open(component, data);
  }

  closeDrawer() {
    this.drawerFacadeService.close();
  }

  showSuccessDialog(
    title: string = 'Success',
    msg: string,
    width?: number,
    img?: string,
    btnTxt?: string,
    action?: DialogRedirectAction,
  ) {
    Swal.fire({
      title: title && this.translateService.instant(title),
      html: msg && this.translateService.instant(msg),
      icon: !img ? 'success' : null,
      imageUrl: img ? img : null,
      confirmButtonColor: '#242f5a',
      confirmButtonText: btnTxt
        ? this.translateService.instant(btnTxt)
        : this.translateService.instant('Ok'),
      showCancelButton: false,
      cancelButtonText: !action?.redirectUrl
        ? btnTxt
          ? this.translateService.instant(btnTxt)
          : this.translateService.instant('Cancel')
        : this.translateService.instant('Cancel'),
      reverseButtons: true,
      width: width,
    }).then((result) => {
      if (!(result.isConfirmed || Swal.DismissReason.backdrop)) return;

      if (action?.redirectUrl) {
        this.router.navigateByUrl(action?.redirectUrl);
      } else if (action?.reload) {
        window.location.reload();
      }
    });
  }

  showInfoDialog(
    title: string = '',
    msg: string,
    width?: number,
    img?: string,
  ) {
    return Swal.fire({
      title: title && this.translateService.instant(title),
      html: this.translateService.instant(msg),
      icon: !img ? 'error' : null,
      imageUrl: img ? img : null,
      confirmButtonColor: '#242f5a',
      confirmButtonText: this.translateService.instant('Ok'),
      showCancelButton: false,
      reverseButtons: true,
      width: width,
    });
  }

  showErrorDialog(msg: ErrorInput, action: Action = null) {
    const obj = msg instanceof Object ? (msg as Record<string, unknown>) : null;
    if (typeof msg === 'string') {
      this.errorService.showErrorDialog(msg, action);
    } else if (obj?.error) {
      const nestedError = obj.error as Record<string, unknown>;
      this.errorService.showErrorDialog(String(nestedError.message), action);
    } else if (obj?.message) {
      this.errorService.showErrorDialog(String(obj.message), action);
    } else {
      this.errorService.showErrorDialog('Something Went Wrong!!', action);
    }
  }

  showConfirmationDialog(
    title: string,
    msg: string,
    width?: string,
    img?: string,
    btnTxt?: string,
    action?: (result: { isConfirmed: boolean }) => void,
  ) {
    return new Promise((resolve, reject) => {
      this.dialog.open(ConfirmationDrawerComponent, {
        width: width || '600px',
        maxWidth: '95vw',
        disableClose: true,
        data: {
          title: this.translateService.instant(title),
          html: this.translateService.instant(msg),
          imageUrl: img,
          showCancelButton: true,
          cancelButtonText: this.translateService.instant('Back'),
          confirmButtonText: this.translateService.instant(btnTxt),
          onConfirm: () => {
            action?.({ isConfirmed: true });
            resolve({ isConfirmed: true });
          },
          onDiscard: () => {
            resolve({});
          },
        },
      });
    });
  }

  notify(message: string, action = 'Close', config: MatSnackBarConfig = {}) {
    return this.snackBar.open(
      this.translateService.instant(message),
      this.translateService.instant(action),
      {
        ...this.snackBarConfig,
        ...config,
      },
    );
  }

  /**
   * convert object properties to different statuses
   * @param obj the object to be converted
   * @param camel set to true if the target should be in camel case status.
   */
  convertProperties(obj: Record<string, unknown>, camel: boolean) {
    for (let prop in obj) {
      let newPropertyName = '';
      if (camel) {
        newPropertyName = this.camelCase(prop);
        obj[newPropertyName] = obj[prop];
      } else {
        newPropertyName = this.capitalize(prop);
        obj[newPropertyName] = obj[prop];
      }
      // if the property was already in the correct case it shouldn't be deleted
      if (prop !== newPropertyName) {
        delete obj[prop];
      }
    }
    return obj;
  }

  private capitalize(str: string) {
    if (str.length) {
      return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
    }
  }

  private camelCase(str: string) {
    if (str.length) {
      return `${str.charAt(0).toLowerCase()}${str.slice(1)}`;
    }
  }

  getHourOfTheDaySlotLabel(hour: number, slotDuration: number) {
    const duration = (hour + slotDuration) % 24;
    return `${this.getHour(hour)} - ${this.getHour(duration)}`;
  }

  getHour(hour: number): string {
    if (hour === 12) {
      return '12:00 PM';
    }
    if (hour === 24 || hour === 0) {
      return '12:00 AM';
    }
    return hour < 12 ? `${hour}:00 AM` : `${hour - 12}:00 PM`;
  }

  /**
   * Compare 2 dates
   * @param date1 date the you want to compare
   * @param date2 the date that you want the endDate to be compared to. default to current date
   * @returns return boolean to indicate if date1 is <= date2
   */
  compareDates(
    date1: Date | string,
    date2: Date | string = new Date(),
  ): boolean {
    const d2 = moment(date2).format('YYYY-MM-DD');
    const d1 = moment(date1).format('YYYY-MM-DD');
    return d1 <= d2;
  }

  /**
   * this function checks the OS type and return information about which OS is used.
   * @returns number 0 -> unrecognized, 1 -> android, 2 -> ios
   */
  checkOperatingSystem() {
    const userAgent = navigator.userAgent || navigator.vendor;

    if (/android/i.test(userAgent)) {
      return OS.ANDROID;
    }
    if (
      /iPad|iPhone|iPod/.test(userAgent) &&
      !(window as Window & { MSStream?: unknown }).MSStream
    ) {
      return OS.IOS;
    }
    return OS.UNRECOGNIZED;
  }

  hasSpecialChars(textStr: string) {
    const specialChars = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
    return specialChars.test(textStr);
  }

  hasNumber(textStr: string) {
    return /\d/.test(textStr);
  }

  hasAlphabets(textStr: string) {
    // Regular expression to match alphabetic characters
    const alphabeticChars = /[a-zA-Z]/;
    return alphabeticChars.test(textStr);
  }

  getPoweredByImage() {
    return this.httpClient
      .get<{ imageUrl: string }>('/org/sgs/configuration/powered-by')
      .pipe(map((res) => res.imageUrl));
  }
}
